/**
 * Optimistic tenancy-name overrides.
 *
 * Friendly customer/org/tenant names render from the id-token `*_name` claims
 * (projected through `<TenantProvider names>` → `useCurrentOrg`). Those claims
 * only change once Auth0 re-mints the token AND the post-login Action re-reads
 * the renamed row — a round-trip that crosses regions, so a name the user just
 * saved can take a beat to appear on token-derived surfaces (the top-bar
 * switcher, the Identity card).
 *
 * This in-memory layer lets a rename handler echo the new name immediately,
 * keyed by entity id. {@link mergeTenancyNames} overlays the overrides onto the
 * token-resolved names for the *active* tenancy quadruple, so every consumer of
 * `useCurrentOrg()` reflects the change on the next render. An override is
 * harmless once the token catches up (it then equals the canonical value) and
 * is wiped by a full reload, which reads the fresh token.
 */
import * as React from "react";
import type { TenancyNames } from "@authe/fe-commons/auth";

interface NameOverridesValue {
  /** Id → freshly-saved friendly name. */
  overrides: Record<string, string>;
  /** Echo a just-saved name for an entity id (no-op for an empty id/name). */
  setName: (id: string | null | undefined, name: string) => void;
}

const EMPTY_OVERRIDES: Record<string, string> = {};
const NOOP_SET_NAME: NameOverridesValue["setName"] = () => {};

const NameOverridesContext = React.createContext<NameOverridesValue | null>(null);

export function NameOverridesProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [overrides, setOverrides] = React.useState<Record<string, string>>({});

  const setName = React.useCallback((id: string | null | undefined, name: string) => {
    const trimmed = name.trim();
    if (!id || !trimmed) return;
    setOverrides((prev) => (prev[id] === trimmed ? prev : { ...prev, [id]: trimmed }));
  }, []);

  const value = React.useMemo<NameOverridesValue>(() => ({ overrides, setName }), [overrides, setName]);
  return <NameOverridesContext.Provider value={value}>{children}</NameOverridesContext.Provider>;
}

/** The current id→name overrides (empty when no provider is mounted). */
export function useNameOverrides(): Record<string, string> {
  return React.useContext(NameOverridesContext)?.overrides ?? EMPTY_OVERRIDES;
}

/** Setter to echo a freshly-saved name (no-op when no provider is mounted). */
export function useSetTenancyName(): NameOverridesValue["setName"] {
  return React.useContext(NameOverridesContext)?.setName ?? NOOP_SET_NAME;
}

/**
 * Overlay id-keyed overrides onto the token-resolved names for the *active*
 * tenancy quadruple. Returns `names` unchanged when there's nothing to apply.
 */
export function mergeTenancyNames(
  names: TenancyNames,
  tenant: { orgId: string | null; tenantId: string | null; customerId: string | null },
  overrides: Record<string, string>,
): TenancyNames {
  if (!Object.keys(overrides).length) return names;
  return {
    ...names,
    orgName: (tenant.orgId && overrides[tenant.orgId]) || names.orgName,
    tenantName: (tenant.tenantId && overrides[tenant.tenantId]) || names.tenantName,
    customerName: (tenant.customerId && overrides[tenant.customerId]) || names.customerName,
  };
}
